import { CalibStep } from '../types';

// Head pose thresholds (degrees) — slightly raised to reduce false positives
export const HEAD_SIDE_THRESH = 15;
export const HEAD_UP_THRESH = 12;
export const HEAD_DOWN_THRESH = 10;
export const HEAD_FAR_THRESH = 22;

// Timing
export const RETURN_WINDOW = 4000;    // ms — 복귀 후 입력까지 여유
export const IDLE_TIMEOUT = 3000;     // ms
export const EMA_ALPHA = 0.3;
export const VOICE_CHECK_INTERVAL = 50; // ms

// History limits
export const STATE_HISTORY_MAX = 40;
export const SCENE_FEED_MAX = 30;
export const SCENE_COOLDOWN = 15000;  // ms between same scene (15초)
export const EVENT_LOG_MAX = 60;

// Paper zone classification (3-zone + duration filter)
export const PAPER_ZONE_MARGIN = 0.15;           // boundary width as fraction of paper
export const PAPER_OUTSIDE_WINDOW_MS = 2000;      // rolling window for duration filter
export const PAPER_OUTSIDE_THRESHOLD = 0.7;       // fraction of window to confirm outside

// Recording demo mode — large webcam-only view with the legacy zone cone.
// Enabled via URL query (`?view=recording`) or path (`/recording`). Used
// for demo capture where the head-pose ray is undesirable and the
// sidebars/calibration overlay should be hidden.
export const IS_RECORDING_MODE =
  typeof window !== 'undefined' &&
  (window.location.search.includes('view=recording') ||
    window.location.pathname.includes('recording'));

// SynthTest mode — replays a pre-recorded webcam video (with synthesized
// corner fiducial tags overlaid) through the live recognition pipeline.
// Used to validate marker / homography / 3-zone behavior on real-world
// frames without needing a live camera. Enter via `?view=synthtest`.
export const IS_SYNTHTEST_MODE =
  typeof window !== 'undefined' &&
  window.location.search.includes('view=synthtest');

// Live demo mode — overlays a synthetic paper + 4-corner fiducial markers
// on top of the live webcam, then pipes the composed stream into the
// recognition pipeline. Used for live presentation of 1꼭지 (paper zone
// detection + boundary handling) and 5꼭지 (hand pose / phone grip /
// tremor). Enter via `?view=livedemo`.
export const IS_LIVEDEMO_MODE =
  typeof window !== 'undefined' &&
  window.location.search.includes('view=livedemo');

// Online-exam rig — moving-content mock exam + BlazeGaze on-screen gaze +
// ScreenGaze region matching + webcam recording. For the phone-on-screen cheat
// recording study. Enter via `?view=onlineexam`.
export const IS_ONLINEEXAM_MODE =
  typeof window !== 'undefined' &&
  window.location.search.includes('view=onlineexam');

// Head-pose ray renderer (precise line replacing sticky zone cone).
// Recording mode inverts these: legacy cone ON, ray OFF.
export const HEADPOSE_RAY_ENABLED = !IS_RECORDING_MODE;
export const HEADPOSE_RAY_SHOW_LEGACY_CONE = IS_RECORDING_MODE;

// Scene detection (improved)
export const SCENE_MAX_GAP_MS = 8000;    // max gap between consecutive matched states
export const SCENE_MIN_HOLD_MS = 300;    // min state duration to count as valid
export const SCENE_MIN_CONFIDENCE = 0.3; // min confidence to accept match

// Calibration steps — 5 sensor sub-steps grouped into 3 visual top-steps
// (신분 확인 / 환경 확인 / 마음 준비) + 1 rPPG placeholder. Order matters:
// eyetracker must run before head so head stability inherits the sample buffer.
export const CALIB_STEPS: CalibStep[] = [
  // ── Step 1 — 신분 확인 ──────────────────────────────────
  {
    dur: 10,
    sensor: 'key',
    title: '시험 체크인',
    desc: '',
    inst: '소속 기관(학교)를 입력해주세요',
    showInput: true,
    showDraw: false,
    showCalibPoints: false,
    topStep: 1,
    topLabel: '신분 확인',
    subLabel: '1/2',
    prompt: '소속 기관(학교)를 키보드로 입력해주세요',
  },
  {
    dur: 8,
    sensor: 'stylus',
    title: '시험 체크인',
    desc: '',
    inst: '이름을 스타일러스 펜으로 써주세요',
    showInput: false,
    showDraw: true,
    showCalibPoints: false,
    topStep: 1,
    topLabel: '신분 확인',
    subLabel: '2/2',
    prompt: '이름을 스타일러스 펜으로 입력해주세요',
  },

  // ── Step 2 — 환경 확인 ──────────────────────────────────
  // Camera angle check: ensure webcam FOV covers both face and desk
  {
    dur: 15,
    sensor: 'camera',
    title: '시험 체크인',
    desc: '',
    inst: '카메라 각도를 확인합니다',
    showInput: false,
    showDraw: false,
    showCalibPoints: false,
    topStep: 2,
    topLabel: '환경 확인',
    prompt: '카메라가 얼굴과 책상을 모두 비추는지 확인합니다',
  },
  // Paper calibration: user clicks edge points → homography
  {
    dur: 30,
    sensor: 'paper',
    title: '시험 체크인',
    desc: '',
    inst: '시험지의 각 변에 2점씩 클릭하세요',
    showInput: false,
    showDraw: false,
    showCalibPoints: false,
    topStep: 2,
    topLabel: '환경 확인',
    prompt: '시험지의 상·우·하·좌 변에 각각 2점씩 클릭하세요 (총 8점)',
  },
  // eyetracker: accumulates head pose samples that `head` will finalize
  {
    dur: 20,
    sensor: 'eyetracker',
    title: '시험 체크인',
    desc: '',
    inst: '화면의 점을 순서대로 클릭해주세요',
    showInput: false,
    showDraw: false,
    showCalibPoints: true,
    topStep: 2,
    topLabel: '환경 확인',
    prompt: '화면 전체에서 나타나는 초록색 점을 눈으로 따라가보세요',
  },
  {
    dur: 6,
    sensor: 'head',
    title: '시험 체크인',
    desc: '',
    inst: '정면을 바라보고 계세요',
    showInput: false,
    showDraw: false,
    showCalibPoints: false,
    topStep: 2,
    topLabel: '환경 확인',
    prompt: '정면을 잠시 바라봐주세요',
  },
  {
    dur: 5,
    sensor: 'voice',
    title: '시험 체크인',
    desc: '',
    inst: '조용히 해주세요 (배경 소음 측정 중)',
    showInput: false,
    showDraw: false,
    showCalibPoints: false,
    topStep: 2,
    topLabel: '환경 확인',
    prompt: '잠시 조용히 해주세요 — 주변 소음을 측정합니다',
  },

  // ── Step 3 — 마음 준비 (rPPG placeholder) ───────────────
  {
    dur: 8,
    sensor: 'rppg',
    title: '시험 체크인',
    desc: '',
    inst: '숨을 깊게 내쉬며 심호흡 해보세요',
    showInput: false,
    showDraw: false,
    showCalibPoints: false,
    topStep: 3,
    topLabel: '마음 준비',
    prompt: '숨을 깊게 내쉬며 심호흡 해보세요',
  },
];

export const CALIB_TOTAL_DURATION = CALIB_STEPS.reduce((s, c) => s + c.dur, 0);

// Eye tracker calibration
export const EYETRACKER_CALIB_POINTS = 9; // 3x3 grid

// Gibberish detection
export const GIBBERISH_WINDOW = 20;
